const GENERAL_TAB = 'General';
const CUSTOM_TAB = 'Custom';
export const tabList = [GENERAL_TAB, CUSTOM_TAB];

/**
 * Validation error definitions with type, message, and severity.
 * @readonly
 */
export const ERROR = {
    SHORT: {
        /** @type {'tooShort'} */
        type: 'tooShort',
        message:
            'This number is too short to be valid, but addition will still be attempted',
        severity: 'warning',
    },
    DUPLICATE: {
        /** @type {'duplicate'} */
        type: 'duplicate',
        message:
            'Duplicate number detected. This first number has already been entered in another row',
        severity: 'error',
    },
    ZERO: {
        /** @type {'startsWithZero'} */
        type: 'startsWithZero',
        message:
            'Please double check this number as there are no country codes that start with a 0',
        severity: 'error',
    },
    COUNTRY_MISMATCH: {
        /** @type {'countryMismatch'} */
        type: 'countryMismatch',
        message:
            'This number belongs to a different country code than other first numbers in the table',
        severity: 'error',
    },
};

/** US and Canada share +1 and are allowed together in the same order */
export const PLUS_ONE_COUNTRIES = new Set(['us', 'ca']);

/**
 * All telephone calling (dial) codes recognised by react-phone-input-2,
 * sorted longest-first for greedy prefix matching against +E.164 numbers.
 * @see getCallingCode
 */
export const CALLING_CODES = [
    '1242',
    '1246',
    '1264',
    '1268',
    '1284',
    '1340',
    '1345',
    '1441',
    '1473',
    '1649',
    '1664',
    '1670',
    '1671',
    '1684',
    '1721',
    '1758',
    '1767',
    '1784',
    '1868',
    '1869',
    '1876',
    '211',
    '212',
    '213',
    '216',
    '218',
    '220',
    '221',
    '222',
    '223',
    '224',
    '225',
    '226',
    '227',
    '228',
    '229',
    '230',
    '231',
    '232',
    '233',
    '234',
    '235',
    '236',
    '237',
    '238',
    '239',
    '240',
    '241',
    '242',
    '243',
    '244',
    '245',
    '246',
    '248',
    '249',
    '250',
    '251',
    '252',
    '253',
    '254',
    '255',
    '256',
    '257',
    '258',
    '260',
    '261',
    '262',
    '263',
    '264',
    '265',
    '266',
    '267',
    '268',
    '269',
    '290',
    '291',
    '297',
    '298',
    '299',
    '350',
    '351',
    '352',
    '353',
    '354',
    '355',
    '356',
    '357',
    '358',
    '359',
    '370',
    '371',
    '372',
    '373',
    '374',
    '375',
    '376',
    '377',
    '378',
    '380',
    '381',
    '382',
    '383',
    '385',
    '386',
    '387',
    '389',
    '420',
    '421',
    '423',
    '500',
    '501',
    '502',
    '503',
    '504',
    '505',
    '506',
    '507',
    '508',
    '509',
    '590',
    '591',
    '592',
    '593',
    '594',
    '595',
    '596',
    '597',
    '598',
    '599',
    '670',
    '672',
    '673',
    '674',
    '675',
    '676',
    '677',
    '678',
    '679',
    '680',
    '681',
    '682',
    '683',
    '685',
    '686',
    '687',
    '688',
    '689',
    '690',
    '691',
    '692',
    '850',
    '852',
    '853',
    '855',
    '856',
    '880',
    '886',
    '960',
    '961',
    '962',
    '963',
    '964',
    '965',
    '966',
    '967',
    '968',
    '970',
    '971',
    '972',
    '973',
    '974',
    '975',
    '976',
    '977',
    '992',
    '993',
    '994',
    '995',
    '996',
    '998',
    '20',
    '27',
    '30',
    '31',
    '32',
    '33',
    '34',
    '36',
    '39',
    '40',
    '41',
    '43',
    '44',
    '45',
    '46',
    '47',
    '48',
    '49',
    '51',
    '52',
    '53',
    '54',
    '55',
    '56',
    '57',
    '58',
    '60',
    '61',
    '62',
    '63',
    '64',
    '65',
    '66',
    '81',
    '82',
    '84',
    '86',
    '90',
    '91',
    '92',
    '93',
    '94',
    '95',
    '98',
    '1',
    '7',
];

/**
 * Extract the calling (dial) code from a +E.164 phone number by greedy
 * longest-prefix match against {@link CALLING_CODES}. Returns '' if the
 * number doesn't start with '+' or no dial code matches.
 * @param {string} number - Phone number in +<dialCode><subscriber> format
 * @returns {string} The dial code without the '+', or ''
 */
export const getCallingCode = (number) => {
    if (!number || !number.startsWith('+')) return '';
    const digits = number.slice(1).replace(/\D/g, '');
    for (const code of CALLING_CODES) {
        if (digits.startsWith(code)) return code;
    }
    return '';
};

/**
 * Find an error definition by its type string.
 * @param {string} type - The error type to look up
 * @returns {Object|undefined} The error definition or undefined
 */
export const getErrorByType = (type) =>
    Object.values(ERROR).find((e) => e.type === type);

export const actionButtons = [
    {
        label: 'Add new block',
        ariaLabel: 'Add',
    },
    {
        label: 'Clear row',
        ariaLabel: 'Clear',
    },
];

export const initTableState = [
    {
        id: 0,
        blockSize: 1,
        number: '',
        countryCode: '',
        error: '',
    },
];

export const blockSizes = [
    { value: 1, name: '1' },
    { value: 5, name: '5' },
    { value: 10, name: '10' },
    { value: 100, name: '100' },
];

export const ocUsageTypes = [
    {
        id: 'CallingUserAssignment',
        displayName: 'Calling User Assignment',
        name: 'Calling User Assignment',
    },
    {
        id: 'FirstPartyAppAssignment',
        displayName: 'First Party App Assignment',
        name: 'First Party App Assignment',
    },
];

export const getErrorStyles = (errorField) => {
    return {
        '& fieldset, & label.MuiFormLabel-root.MuiInputLabel-root': {
            borderColor: errorField ? 'red' : null,
            color: errorField ? 'red' : null,
        },
    };
};

export const STATUS_PILL_MAP = {
    OK: {
        tag: { backgroundColor: '#d4f5d4', border: '1.5px solid #52a552' },
        circle: { backgroundColor: '#52a552' },
    },
    Complete: {
        tag: { backgroundColor: '#d4f5d4', border: '1.5px solid #52a552' },
        circle: { backgroundColor: '#52a552' },
    },
    Error: {
        tag: {
            backgroundColor: 'rgb(255, 0, 0, 0.05)',
            border: '1.5px solid #d64759',
        },
        circle: { backgroundColor: '#d64759' },
    },
    Partial: {
        tag: { backgroundColor: '#d6eeff', border: '1.5px solid #4a9fd4' },
        circle: { backgroundColor: '#4a9fd4' },
    },
    Processing: {
        tag: { backgroundColor: '#fefbd4', border: '1.5px solid #c9b832' },
        circle: { backgroundColor: '#c9b832' },
    },
    Pending: {
        tag: { backgroundColor: '#fefbd4', border: '1.5px solid #c9b832' },
        circle: { backgroundColor: '#c9b832' },
    },
};

export const NAVIGATE_AWAY_WARNING =
    'Do not navigate away from this page. To check another area, open a new window, tab, or browser instead — navigating away will interrupt the upload.';

export const PROGRESS_COMPLETE_DELAY_MS = 800;

export const getValidationColors = (
    isInvalid,
    isWarning,
    darkMode,
    // muted: softer palette for read-only cells (e.g. LastNumberCell)
    { muted = false } = {},
) => {
    const borderColor = isInvalid
        ? isWarning
            ? '#ECC94B'
            : muted
              ? '#FC8181'
              : '#E53E3E'
        : darkMode
          ? 'var(--chakra-colors-dark-tcap-borderGray)'
          : 'var(--chakra-colors-gray-200)';
    const bgColor = isInvalid
        ? darkMode
            ? isWarning
                ? `rgba(236, 201, 75, ${muted ? 0.08 : 0.15})`
                : `rgba(229, 62, 62, ${muted ? 0.08 : 0.15})`
            : isWarning
              ? muted
                  ? '#FFFFF0'
                  : '#FEFCBF'
              : muted
                ? '#FFF5F5'
                : '#FED7D7'
        : darkMode
          ? 'var(--chakra-colors-dark-tcap)'
          : null;
    const focusBorderColor = isInvalid
        ? isWarning
            ? 'var(--chakra-colors-yellow-400)'
            : 'var(--chakra-colors-red-500)'
        : 'var(--chakra-colors-messenger-500)';
    const textColor = isInvalid
        ? muted && darkMode
            ? isWarning
                ? '#ECC94B'
                : '#FC8181'
            : isWarning
              ? '#B7791F'
              : muted
                ? '#C53030'
                : '#E53E3E'
        : muted
          ? darkMode
              ? 'var(--chakra-colors-gray-400)'
              : 'var(--chakra-colors-gray-500)'
          : darkMode
            ? 'white'
            : 'black';
    return { bgColor, borderColor, focusBorderColor, textColor };
};
